import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Label } from "@activecollab/components";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import classNames from "classnames";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import Angie from "~/utils/angie";

const PublicStripeCardForm = ({ onSubmit }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidCardNumber, setIsValidCardNumber] = useState(false);
  const [isValidCardExpiration, setIsValidCardExpiration] = useState(false);
  const [isValidCVV, setIsValidCVV] = useState(false);
  const [cardHolder, setCardHolder] = useState("");

  const onCardNumberChange = useCallback((e) => {
    setIsValidCardNumber(e?.complete);
  }, []);
  const onCardExpirationChange = useCallback((e) => {
    setIsValidCardExpiration(e?.complete);
  }, []);
  const onCardCvvChange = useCallback((e) => {
    setIsValidCVV(e?.complete);
  }, []);
  const onCardHolderChange = useCallback((e) => {
    setCardHolder(e?.target?.value?.trim());
  }, []);

  const onSubmitCallback = useCallback(
    (e) => {
      setIsSubmitting(true);
      e.preventDefault();
      if (!stripe || !elements) {
        return;
      }
      stripe
        .createToken(elements?.getElement(CardExpiryElement), {
          name: cardHolder,
        })
        .then((stripeResult) => {
          if (!stripeResult.error && stripeResult.token) {
            onSubmit(stripeResult.token, setIsSubmitting);
          }
          if (stripeResult.error) {
            Angie.logger.error("Error in payment", {
              error: stripeResult.error,
            });
            showErrorToast(t("Failed to process payment"));
          }
        });
    },
    [cardHolder, elements, onSubmit, stripe, t]
  );

  const stripeElementStyles = useMemo(() => {
    return {
      style: {
        base: {
          color: "#303037",
          "::placeholder": {
            color: "rgba(48,48,55,0.34)",
            fontWeight: 300,
            fontSize: 14,
          },
        },
      },
    };
  }, []);

  return (
    <form
      id="stripe_form"
      onSubmit={onSubmitCallback}
      className="tw-flex tw-flex-col tw-gap-4 tw-w-full"
    >
      <div>
        <Label>{t("Cardholder")}</Label>
        <Input
          className="tw-w-full tw-shrink-0 tw-border-gray-300 tw-p-2"
          onChange={onCardHolderChange}
        />
      </div>
      <div>
        <Label>{t("Card Number")}</Label>
        <CardNumberElement
          onChange={onCardNumberChange}
          options={stripeElementStyles}
          className={classNames(
            "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 stripeElements"
          )}
        />
      </div>
      <div className="tw-flex tw-flex-1">
        <div className="tw-flex tw-flex-col tw-flex-1">
          <Label>{t("Expires")}</Label>
          <CardExpiryElement
            onChange={onCardExpirationChange}
            options={stripeElementStyles}
            className={classNames(
              "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 tw-mr-5 tw-align-middle tw-flex-1 stripeElements"
            )}
          />
        </div>
        <div className="tw-flex tw-flex-col tw-flex-1">
          <Label>{t("Security Code")}</Label>
          <CardCvcElement
            onChange={onCardCvvChange}
            options={stripeElementStyles}
            className={classNames(
              "tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-h-8 tw-p-2 tw-flex-1 stripeElements"
            )}
          />
        </div>
      </div>
      <Button
        onClick={onSubmitCallback}
        disabled={!isValidCardExpiration || !isValidCardNumber || !isValidCVV || !cardHolder || isSubmitting}
        className="tw-max-w-full tw-w-full"
        size="big"
      >
        {t("Pay Now")}
      </Button>
    </form>
  );
};

PublicStripeCardForm.propTypes = {
  onSubmit: PropTypes.func,
};

export default PublicStripeCardForm;
